import { db } from './schema'
import { createId } from '@/utils/id'

/**
 * Registro de errores local (observabilidad sin cuenta externa).
 *
 * Captura errores no controlados de la app y los persiste en IndexedDB
 * para que el usuario (y el desarrollador) pueda diagnosticar problemas
 * sin enviar datos a terceros. Es deliberadamente local: nada sale del
 * dispositivo salvo que el usuario exporte el registro.
 *
 * Se instala desde main.ts: captura `window.onerror`, promesas rechazadas
 * sin handler, y errores de componentes vía `app.config.errorHandler`.
 */

export type ErrorLevel = 'error' | 'warning' | 'info'

export interface ErrorLogEntry {
  id: string
  /** ISO timestamp del error. */
  timestamp: string
  level: ErrorLevel
  /** Mensaje corto (mensaje del error o resumen). */
  message: string
  /** Stack trace si está disponible. */
  stack?: string
  /** Orente: qué subsistema lo reportó (sync, ai, epub, reader, app…). */
  source?: string
  /** Información de contexto adicional (string). */
  context?: string
  /** Versión de la app en el momento del error. */
  appVersion?: string
  /** Si el usuario ya lo ha visto en el panel. */
  seen: boolean
}

/** Cap máximo de entradas guardadas para no llenar IndexedDB. */
const MAX_ENTRIES = 200

/** Registra un error en el log local. No lanza nunca. */
export async function logError(entry: {
  level?: ErrorLevel
  message: string
  stack?: string
  source?: string
  context?: string
}): Promise<void> {
  try {
    const record: ErrorLogEntry = {
      id: createId(),
      timestamp: new Date().toISOString(),
      level: entry.level ?? 'error',
      message: entry.message.slice(0, 500),
      stack: entry.stack?.slice(0, 4000),
      source: entry.source,
      context: entry.context?.slice(0, 1000),
      appVersion: APP_VERSION,
      seen: false,
    }
    await db.errorLogs.put(record)
    // Recortar si excede el cap (borrar los más antiguos).
    const count = await db.errorLogs.count()
    if (count > MAX_ENTRIES) {
      const oldest = await db.errorLogs.orderBy('timestamp').limit(count - MAX_ENTRIES).primaryKeys()
      await db.errorLogs.bulkDelete(oldest)
    }
  } catch {
    // Si el propio logging falla (p. ej. IndexedDB roto), no hay mucho
    // que hacer; caemos al console.error silencioso.
  }
}

/** Devuelve los errores recientes (más nuevos primero). */
export async function getErrorLogs(limit = 50): Promise<ErrorLogEntry[]> {
  return db.errorLogs.orderBy('timestamp').reverse().limit(limit).toArray()
}

/** Cuenta los errores no vistos (para un badge en ajustes). */
export async function countUnseenErrors(): Promise<number> {
  // `seen` es boolean y no está indexado como tal; filtramos en memoria
  // (el cap de 200 entradas lo mantiene barato).
  const all = await db.errorLogs.toArray()
  return all.filter((e) => !e.seen).length
}

/** Marca todos como vistos. */
export async function markAllSeen(): Promise<void> {
  await db.errorLogs.toCollection().modify({ seen: true })
}

/** Borra todo el registro. */
export async function clearErrorLogs(): Promise<void> {
  await db.errorLogs.clear()
}

/**
 * Instala los capturadores globales. Llamar una sola vez desde main.ts.
 * Devuelve una función para registrar manualmente desde errorHandler de Vue.
 */
export function installGlobalErrorCapture() {
  // Silenciar el falso positivo "ResizeObserver loop completed with
  // undelivered notifications". Es un warning BENIGNO que el navegador emite
  // cuando un observer dispara un re-layout que vuelve a dispararlo; se
  // autolimita y no afecta a la app. Patch estándar: envolver el callback
  // del ResizeObserver para tragarse esa excepción concreta antes de que
  // llegue a window.onerror (y así no contamine el panel de Diagnóstico).
  if (typeof ResizeObserver !== 'undefined') {
    const Native = window.ResizeObserver
    // @ts-expect-error — firma flexible del wrapper
    window.ResizeObserver = function (callback: ResizeObserverCallback) {
      const wrapped: ResizeObserverCallback = (entries, obs) => {
        try {
          callback(entries, obs)
        } catch (e) {
          // Solo tragamos el aviso de bucle de ResizeObserver; el resto se
          // relanza para que window.onerror lo capture normalmente.
          if (!(e instanceof Error) || !/ResizeObserver loop/i.test(e.message)) throw e
        }
      }
      return new Native(wrapped)
    }
    // Conservar prototipo/statics para que `instanceof` y APIs sigan funcionando.
    window.ResizeObserver.prototype = Native.prototype
  }

  // Mensajes de error benignos que no deben ensuciar el panel de Diagnóstico.
  const NOISE_PATTERNS: RegExp[] = [
    /ResizeObserver loop completed with undelivered notifications/i,
    /ResizeObserver loop limit exceeded/i,
  ]
  function isNoise(message: string): boolean {
    return NOISE_PATTERNS.some((re) => re.test(message))
  }

  // Errores síncronos no capturados.
  window.addEventListener('error', (event) => {
    if (isNoise(event.message || '')) return
    void logError({
      message: event.message || 'Error no capturado',
      stack: event.error?.stack,
      source: 'window.onerror',
    })
  })
  // Promesas rechazadas sin handler.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const message =
      (reason instanceof Error ? reason.message : String(reason)) ||
      'Promesa rechazada sin handler'
    if (isNoise(message)) return
    void logError({
      message,
      stack: reason instanceof Error ? reason.stack : undefined,
      source: 'unhandledrejection',
    })
  })
}

/** Handler para app.config.errorHandler de Vue. */
export function vueErrorHandler(
  err: unknown,
  _vm: unknown,
  info: string,
): void {
  void logError({
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? err.stack : undefined,
    source: 'vue',
    context: info,
  })
}

/** Versión de la app (para adjuntar a los logs). */
const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string) ?? '0.1.0'
